import { InfoIcon } from '@phosphor-icons/react'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { DropdownFilter, type DropdownElement } from '../../components/dropdown-filter.tsx'
import { Checkbox } from '../../components/input/checkbox.tsx'
import { SearchInput } from '../../components/input/search-input.tsx'
import { QuickViewSelector } from '../../components/menus/quick-view-selector.tsx'
import { TooltipWithHover } from '../../components/tooltip.tsx'
import { UNGROUPED_PROJECT_FILTER, useGetProjects } from '../../utils/api-client/api-hooks.ts'
import * as styles from './repositories-controls.css.ts'
import { repositoryQuickViewItems, type RepositoryQuickViewId } from './repository-quick-views.ts'
import { RISK_BANDS, riskBandLabel, type RiskBandId } from './repository-risk.ts'

type RepositoriesControlsProps = {
  /** Omitted while the count is still loading, so the heading does not flash a zero. */
  count?: number
  quickView: RepositoryQuickViewId
  onChangeQuickView: (view: RepositoryQuickViewId) => unknown
  /**
   * Whether the estate has enough scored repositories to rank. Below the threshold the ranked view is not
   * offered and the filter — whose only group is the score bands — is not shown at all.
   */
  isRiskAvailable: boolean
  selectedBands: RiskBandId[]
  onToggleBand: (band: RiskBandId) => unknown
  onResetBands: () => unknown
  /** Project ids, or {@link UNGROUPED_PROJECT_FILTER}, the list is narrowed to. */
  selectedProjectIds: string[]
  onToggleProject: (projectId: string) => unknown
  onResetProjects: () => unknown
  /** Ranked view only: off by default, so the ranking is a ranking of scored repositories. */
  isShowingUnranked: boolean
  onToggleShowUnranked: (isShowing: boolean) => unknown
  searchTerm: string
  onChangeSearchTerm: (value: string) => unknown
  onOpenAddRepository: () => unknown
  description: string
  /**
   * Whether any connected repository is an Azure DevOps one. Azure has its own notion of a project,
   * so where both are on screen the filter group says which of the two it means.
   */
  hasAzureRepositories?: boolean
}

/**
 * Says which "project" the filter group means, for an estate that also has Azure DevOps projects.
 * Azure calls its own repository container a project, and the two are unrelated.
 */
const AzureProjectDisambiguation: React.FC = () => (
  <TooltipWithHover
    size="small"
    trigger={
      <span aria-label="About Pixee projects" role="img">
        <InfoIcon size={14} weight="regular" />
      </span>
    }
  >
    A Pixee project groups repositories to scope feedback. It is not your Azure DevOps project.
  </TooltipWithHover>
)

/**
 * The repositories page's header and action bar.
 *
 * Follows the findings table's controls: the quick view selector names what you are looking at on the
 * left, and search plus the filter dropdown sit on the right. It replaced a bespoke two-option switch —
 * the quick view carries an ordering and a filter state with it, which a toggle cannot.
 */
export const RepositoriesControls: React.FC<RepositoriesControlsProps> = ({
  count,
  quickView,
  onChangeQuickView,
  isRiskAvailable,
  selectedBands,
  onToggleBand,
  onResetBands,
  selectedProjectIds,
  onToggleProject,
  onResetProjects,
  isShowingUnranked,
  onToggleShowUnranked,
  searchTerm,
  onChangeSearchTerm,
  onOpenAddRepository,
  description,
  hasAzureRepositories = false,
}) => {
  const { projects } = useGetProjects()
  const hasProjects = projects.length > 0

  const projectGroup: DropdownElement | undefined = hasProjects
    ? {
        type: 'group',
        id: 'project',
        text: hasAzureRepositories ? 'Pixee project' : 'Project',
        icon: hasAzureRepositories ? <AzureProjectDisambiguation /> : undefined,
        // Hundreds of projects do not fit a scrollable panel, so the group filters its own options.
        searchLabel: 'Search projects',
        onToggleGroup: onResetProjects,
        filters: [
          ...projects.map(project => ({
            type: 'checkbox' as const,
            id: `project-${project.id}`,
            text: project.name,
            value: selectedProjectIds.includes(project.id),
            onToggle: () => onToggleProject(project.id),
          })),
          {
            type: 'checkbox' as const,
            id: 'project-ungrouped',
            text: 'Ungrouped',
            value: selectedProjectIds.includes(UNGROUPED_PROJECT_FILTER),
            onToggle: () => onToggleProject(UNGROUPED_PROJECT_FILTER),
          },
        ],
      }
    : undefined

  const bandGroup: DropdownElement | undefined = isRiskAvailable
    ? {
        type: 'group',
        id: 'band',
        text: 'Risk score',
        onToggleGroup: onResetBands,
        // The range alone, with no Critical/High/… names: those belong to a finding's severity, which is a
        // different scale measured differently, and sharing the words would imply the two convert.
        filters: RISK_BANDS.map(band => ({
          type: 'checkbox' as const,
          id: `band-${band.id}`,
          text: riskBandLabel(band),
          value: selectedBands.includes(band.id),
          onToggle: () => onToggleBand(band.id),
        })),
      }
    : undefined

  const filterElements: DropdownElement[] = []
  if (projectGroup) {
    filterElements.push(projectGroup)
  }
  if (bandGroup) {
    filterElements.push(bandGroup)
  }
  const resetAllFilters = () => {
    onResetProjects()
    onResetBands()
  }

  return (
    <div className={styles.pageHead}>
      <div className={styles.header}>
        <h4 className={styles.heading}>Repositories</h4>
        <p className={styles.description}>{description}</p>
      </div>

      <div className={styles.controls}>
        <div className={styles.left}>
          <QuickViewSelector<RepositoryQuickViewId>
            quickViews={repositoryQuickViewItems(isRiskAvailable)}
            selectedQuickView={quickView}
            onSelectedQuickViewChange={onChangeQuickView}
            currentCount={count}
          />

          {/* Beside the view it qualifies, not among the search and filter controls: it says what this
              ranking leaves out. Ranked view only — in the unordered list nothing is ranked, so it would
              toggle nothing. Unchecked by default, and the checkbox is what tells a reader unscored
              repositories are being left out. */}
          {quickView === 'ranked-by-risk' && (
            <div className={styles.rankingQualifiers}>
              <label className={styles.showUnranked}>
                <Checkbox
                  id="show-unranked"
                  checked={isShowingUnranked}
                  onCheckedChange={onToggleShowUnranked}
                  ariaLabel="Show unranked"
                />
                Show unranked
              </label>

              {/* The ranking orders the rows already fetched, not the estate: the list is paginated
                  server-side, so ordering has to happen before pagination to rank across pages. Said
                  here rather than left to be inferred, because page one otherwise reads as the estate's
                  riskiest. Comes out when the API can order. */}
              <span className={styles.rankingScope}>Ranked per page</span>
            </div>
          )}
        </div>

        <div className={styles.right}>
          <div className={styles.searchSlot}>
            <SearchInput
              value={searchTerm}
              onChange={onChangeSearchTerm}
              size="small"
              debounce={300}
              name="Search repositories"
            />
          </div>

          {/* Shown once it has a group to open onto: the project group needs a project to exist, and the
              risk-score group needs enough scored repositories to rank. */}
          {filterElements.length > 0 && (
            <DropdownFilter
              filterElements={filterElements}
              onToggleAllFiltersOff={resetAllFilters}
              isAnyNonDefaultFilterActive={selectedBands.length > 0 || selectedProjectIds.length > 0}
            />
          )}
          <DefaultButton size="small" onClick={onOpenAddRepository}>
            + &nbsp; Connect new
          </DefaultButton>
        </div>
      </div>
    </div>
  )
}
